/**
 * 帮助中心 i18n（界面文案 + 内容语言）
 *
 * 与平台其它应用**互不影响**：刻意不使用 `odc-shared/i18n` 的 `initI18n` / `changeLanguage`
 * （它们把语言写死在 `odc_language`，同域下该 key 由「变现端（AdNet，/publisher）」使用，
 * 共用会互相带偏）。这里只用 i18next 原生能力；本地只记「上次选择」（独立 key），仅用于补全 URL。
 *
 * 语言**只有一个来源：URL 的 `?language=`**（缺省英文，与 useSearch 同一口径）：
 * 同一链接在任何机器 / 标签页打开都是同一种语言。站内内页链接由 buildLanguageQuery 带上语言，
 * 所以刷新与分享都不丢。界面文案（i18next）与内容语言都由它驱动，切换器一次点击两者同时生效。
 */
import i18next from 'i18next';
import { ELanguage } from 'odc-shared/i18n';

import { resources } from '@src/locales';
import { BASE_LANGUAGE, isLanguageEnabled, normalizeLanguage } from '@src/pages/supportCenter/constants/language';

/** 合法化语言值：URL 脏值、或语言被回滚（列表里已没有）时，一律回落基准语言 */
export function pickEnabledLanguage(value: string | null | undefined): ELanguage {
  return isLanguageEnabled(value ?? undefined) ? (value as ELanguage) : BASE_LANGUAGE;
}

/**
 * 初始语言 = URL 的 `?language=`（缺省英文）
 *
 * 必须在首次渲染前就得对：若 i18next 启动时用别的来源（如本地偏好），首帧会渲染成另一种语言，
 * 而这一帧的纠正发生在 useLayoutEffect 里、早于 useLocale 注册监听（useEffect），组件收不到
 * 通知，要等下一次重渲染才变过来 —— 表现就是"冷启动闪一下另一种语言"。
 */
export function getLanguageFromUrl(search: string = window.location.search): ELanguage {
  return pickEnabledLanguage(new URLSearchParams(search).get('language'));
}

/** 上次主动选择的语言（**不是语言来源**，只用于进入帮助中心时补全 URL，见 normalizeHelpCenterUrl） */
const STORAGE_KEY = 'odc_help_center_language';

/** 记录用户主动选择的语言（切换器里调用） */
export function rememberHelpCenterLanguage(language: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, pickEnabledLanguage(language));
  } catch {
    // localStorage 不可用时忽略：语言已由 URL 承载
  }
}

/**
 * 进入帮助中心时按上次选择补全 URL（必须在渲染前、setupHelpCenterI18n 之前调用，才不会闪）
 *
 * - 只在帮助中心内页（`/supportcenter`）生效：官网首页 / 产品页保持英文，不被偏好带偏；
 * - URL 已带 language 就尊重链接（分享链接的语言优先）；
 * - 用 replaceState：不产生历史记录，地址栏即最终语言，复制出去仍自带语言。
 */
export function normalizeHelpCenterUrl(): void {
  const { pathname, search } = window.location;
  if (!pathname.startsWith('/supportcenter')) return;

  const params = new URLSearchParams(search);
  if (params.get('language')) return;

  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage 不可用（隐私模式 / 被禁用）：按「没有偏好」继续，URL 仍要显式带上语言
  }
  // 语言在 URL 里始终显式（含 en）：复制 / 分享出去的地址栏链接自带语言，不会被接收方的本地偏好改掉
  // （"缺省=英文"只用于改造前的老链接兜底）
  params.set('language', pickEnabledLanguage(stored));
  // 保留 hash：帮助中心正文的右侧导航与深链都靠它定位，重建 URL 时不能丢
  window.history.replaceState(null, '', `${pathname}?${params.toString()}${window.location.hash}`);
}

/** 初始化 i18n。必须在首次渲染前调用一次 */
export function setupHelpCenterI18n(): void {
  void i18next.init({
    lng: getLanguageFromUrl(),
    fallbackLng: BASE_LANGUAGE,
    defaultNS: 'common',
    fallbackNS: 'common',
    ns: ['common', 'helpCenter'],
    nsSeparator: false,
    keySeparator: false,
    resources,
    interpolation: {
      escapeValue: false,
    },
  });
}

/**
 * 只切界面语言（i18next），不写任何存储。
 *
 * 用于"语言以 URL 为唯一来源"：帮助中心内页按 `?language=` 同步界面文案，
 * 这样分享链接（URL 是 zh、对方本地是 en）看到的界面与内容也是一致的。
 */
export function applyHelpCenterLanguage(language: string): ELanguage {
  const next = normalizeLanguage(language);
  if (i18next.language !== next) {
    void i18next.changeLanguage(next);
  }
  return next;
}

/**
 * 取"指定语言"下的翻译函数，不依赖全局语言。
 *
 * 给那些语言由 URL / 入参决定、而全局 i18next 可能还是另一种语言的地方用
 * （如首页路由下的「全部结果页」，它跟随发起端语言，而全局语言受上次内页访问影响）。
 */
export function getHelpCenterT(language: string): (key: string) => string {
  const fixedT = i18next.getFixedT(normalizeLanguage(language), 'helpCenter');
  return (key: string) => fixedT(key);
}
